/**
 * TrySoro Blog API helpers.
 *
 * TrySoro doesn't expose a clean JSON list endpoint — instead they serve a
 * dynamic JS file with `SORO_ARTICLES = [...]` baked in. We fetch that JS,
 * extract the array via regex, and parse it as JSON so we can render the
 * blog natively in React (instead of letting their script inject styled
 * HTML into our DOM).
 *
 * Single-article content IS exposed via a clean JSON endpoint, so we use
 * that directly.
 */

const SORO_TOKEN = "2394caba-5ffd-41ea-92bf-da8f3b62f649";
const SORO_API_BASE = "https://app.trysoro.com";
const EMBED_URL = `${SORO_API_BASE}/api/embed/${SORO_TOKEN}`;
const ARTICLE_URL = (id) =>
    `${SORO_API_BASE}/api/embed/${SORO_TOKEN}/article/${encodeURIComponent(id)}`;

// In-memory cache (per page session). Avoids refetching the embed JS on
// every navigation between /blog and /blog/:slug.
let listCachePromise = null;
const contentCache = new Map();

/**
 * Fetch the list of published articles.
 * Returns: Promise<Array<{id, slug, title, excerpt, date, isoDate, image, content?}>>
 */
export function fetchArticles({ bust = false } = {}) {
    if (bust) listCachePromise = null;
    if (listCachePromise) return listCachePromise;

    listCachePromise = fetch(EMBED_URL, { credentials: "omit" })
        .then((r) => {
            if (!r.ok) throw new Error(`Soro embed fetch ${r.status}`);
            return r.text();
        })
        .then((js) => {
            // Extract `var SORO_ARTICLES = [...]` — be lenient about
            // whitespace and quoting. The script is generated server-side
            // by TrySoro and always uses double-quoted JSON-safe values.
            const match = js.match(
                /var\s+SORO_ARTICLES\s*=\s*(\[[\s\S]*?\])\s*;/,
            );
            if (!match) return [];
            try {
                const arr = JSON.parse(match[1]);
                return Array.isArray(arr) ? arr : [];
            } catch (_e) {
                // Last-resort: try eval-via-Function (safe because we control
                // the regex shape and the script source is HTTPS).
                try {
                    // eslint-disable-next-line no-new-func
                    const arr = Function(`"use strict"; return (${match[1]})`)();
                    return Array.isArray(arr) ? arr : [];
                } catch (_e2) {
                    return [];
                }
            }
        })
        .catch((err) => {
            // Reset so subsequent calls can retry.
            listCachePromise = null;
            throw err;
        });

    return listCachePromise;
}

/**
 * Fetch HTML content for a single article.
 * Returns: Promise<string>  (sanitisation note: HTML is rendered as-is via
 * dangerouslySetInnerHTML — TrySoro is the trusted author here.)
 */
export function fetchArticleContent(article) {
    if (!article) return Promise.reject(new Error("article required"));

    // Some articles ship `content` inline (server-side preload for SEO);
    // use that if present.
    if (article.content) {
        contentCache.set(article.id, article.content);
        return Promise.resolve(article.content);
    }
    if (contentCache.has(article.id)) {
        return Promise.resolve(contentCache.get(article.id));
    }
    return fetch(ARTICLE_URL(article.id), { credentials: "omit" })
        .then((r) => {
            if (!r.ok) throw new Error(`Article fetch ${r.status}`);
            return r.json();
        })
        .then((data) => {
            const html = (data && data.content) || "";
            contentCache.set(article.id, html);
            return html;
        });
}

/** Find an article by slug from the cached list (cheap lookup). */
export async function findArticleBySlug(slug) {
    const list = await fetchArticles();
    return list.find((a) => a.slug === slug) || null;
}

/** Format the article's reading time given content HTML (rough estimate). */
export function readingTime(html) {
    if (!html) return null;
    const text = String(html).replace(/<[^>]+>/g, " ");
    const words = text.split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.round(words / 220));
    return `${mins} min read`;
}
